import React, { useState, useRef, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { useLanguage } from '../context/LanguageContext';

export default function ContactSection() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef();

  useEffect(() => {
    try {
      emailjs.init({ publicKey: t.personalInfo.emailjs.publicKey });
    } catch (e) {
      console.warn('EmailJS init warning:', e);
    }
  }, [t.personalInfo.emailjs.publicKey]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'user_name') {
      setFormData((prev) => ({ ...prev, name: value }));
    } else if (name === 'user_mail') {
      setFormData((prev) => ({ ...prev, email: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(t.personalInfo.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      window.location.href = `mailto:${t.personalInfo.email}`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    const now = new Date();
    const timeString = now.toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' });

    try {
      // 1. SendForm attempt
      const response = await emailjs.sendForm(
        t.personalInfo.emailjs.serviceId,
        t.personalInfo.emailjs.templateId,
        formRef.current,
        {
          publicKey: t.personalInfo.emailjs.publicKey,
        }
      );
      console.log('EmailJS sendForm SUCCESS:', response.status, response.text);
      setIsSubmitting(false);
      setStatus({
        type: 'success',
        message: t.ui.formSuccess,
      });
      setFormData({ name: '', email: '', message: '' });
    } catch (error1) {
      console.warn('EmailJS sendForm failed, attempting direct JSON payload...', error1);
      try {
        // 2. Direct JSON send fallback
        const payload = {
          time: timeString,
          user_name: formData.name.trim(),
          user_mail: formData.email.trim(),
          message: formData.message.trim(),
        };

        const response2 = await emailjs.send(
          t.personalInfo.emailjs.serviceId,
          t.personalInfo.emailjs.templateId,
          payload,
          t.personalInfo.emailjs.publicKey
        );
        console.log('EmailJS direct send SUCCESS:', response2.status, response2.text);
        setIsSubmitting(false);
        setStatus({
          type: 'success',
          message: t.ui.formSuccess,
        });
        setFormData({ name: '', email: '', message: '' });
      } catch (error2) {
        console.error('EmailJS submission failed:', error2);
        setIsSubmitting(false);
        setStatus({
          type: 'error',
          message: t.ui.formError,
        });
      }
    }
  };

  const mailtoHref = `mailto:${t.personalInfo.email}?subject=${encodeURIComponent(
    `Engineering Inquiry - ${formData.name || 'Visitor'}`
  )}&body=${encodeURIComponent(
    `Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
  )}`;

  return (
    <section className="section-wrapper reveal-on-scroll" id="iletisim" style={{ paddingBottom: '7rem' }}>
      <div className="container">
        <div className="section-header-neo">
          <div>
            <div className="neo-tag-pill">
              <span>[ {t.ui.contactLabel} ]</span>
            </div>
            <h2 className="neo-section-title">{t.ui.contactTitle}</h2>
          </div>
          <p className="neo-section-sub">{t.ui.contactSubtitle}</p>
        </div>

        <div className="contact-neo-layout">
          {/* Left Panel */}
          <div className="contact-neo-left spotlight-card">
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'var(--accent-acid)', fontWeight: 700 }}>
                :: {t.ui.directEmail}
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.05rem', fontWeight: 700, color: '#ffffff', wordBreak: 'break-all', marginTop: '0.35rem' }}>
                {t.personalInfo.email}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn-neo btn-neo-dark btn-sweep"
                onClick={copyEmail}
                style={{ fontSize: '0.84rem' }}
              >
                <i className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'}`}></i>
                <span>{copied ? t.ui.copied : t.ui.copyEmail}</span>
              </button>
              <a
                href={`mailto:${t.personalInfo.email}`}
                className="btn-neo btn-neo-chrome btn-sweep"
                style={{ fontSize: '0.84rem' }}
              >
                <span>{t.ui.openMailClient}</span>
                <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: '0.75rem' }}></i>
              </a>
            </div>

            <hr style={{ borderColor: '#282d3b', margin: '0.5rem 0' }} />

            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: '#8b92a4', fontWeight: 700, marginBottom: '0.75rem' }}>
                :: {t.ui.socialsHeading}
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <a
                  href={t.personalInfo.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-neo btn-neo-dark tag-spring"
                  style={{ fontSize: '0.84rem' }}
                >
                  <i className="fa-brands fa-linkedin" style={{ color: '#60a5fa' }}></i>
                  <span>LinkedIn ↗</span>
                </a>
                <a
                  href={t.personalInfo.github}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-neo btn-neo-dark tag-spring"
                  style={{ fontSize: '0.84rem' }}
                >
                  <i className="fa-brands fa-github"></i>
                  <span>GitHub ↗</span>
                </a>
              </div>
            </div>
          </div>

          {/* Right Form Panel */}
          <div className="contact-neo-right spotlight-card">
            <form ref={formRef} onSubmit={handleSubmit}>
              <input
                type="hidden"
                name="time"
                value={new Date().toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}
              />

              <div className="neo-field-wrap">
                <label className="neo-field-label" htmlFor="user_name">
                  {t.ui.formName}
                </label>
                <input
                  id="user_name"
                  type="text"
                  name="user_name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder={t.ui.formPlaceholderName}
                  className="neo-field-input"
                />
              </div>

              <div className="neo-field-wrap">
                <label className="neo-field-label" htmlFor="user_mail">
                  {t.ui.formEmail}
                </label>
                <input
                  id="user_mail"
                  type="email"
                  name="user_mail"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder={t.ui.formPlaceholderEmail}
                  className="neo-field-input"
                />
              </div>

              <div className="neo-field-wrap">
                <label className="neo-field-label" htmlFor="user_message">
                  {t.ui.formMessage}
                </label>
                <textarea
                  id="user_message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  placeholder={t.ui.formPlaceholderMessage}
                  className="neo-field-textarea"
                />
              </div>

              {status.message && (
                <div
                  style={{
                    padding: '0.85rem 1.15rem',
                    marginBottom: '1.25rem',
                    border: '2px solid',
                    borderColor: status.type === 'success' ? '#ccff00' : '#f87171',
                    background: status.type === 'success' ? 'rgba(204,255,0,0.1)' : 'rgba(248,113,113,0.1)',
                    color: status.type === 'success' ? '#ccff00' : '#fca5a5',
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                  }}
                >
                  <i className={`fa-solid ${status.type === 'success' ? 'fa-circle-check' : 'fa-triangle-exclamation'}`}></i>
                  <div style={{ flex: 1 }}>{status.message}</div>
                  {status.type === 'error' && (
                    <a
                      href={mailtoHref}
                      className="btn-neo btn-neo-chrome"
                      style={{ padding: '0.35rem 0.75rem', fontSize: '0.78rem', whiteSpace: 'nowrap' }}
                    >
                      {t.ui.formSendViaMail}
                    </a>
                  )}
                </div>
              )}

              <button
                type="submit"
                className="btn-neo btn-neo-acid btn-sweep"
                disabled={isSubmitting}
                style={{ width: '100%', padding: '0.95rem' }}
              >
                {isSubmitting ? (
                  <>
                    <i className="fa-solid fa-circle-notch fa-spin"></i>
                    <span>{t.ui.formSending}</span>
                  </>
                ) : (
                  <>
                    <span>[ {t.ui.formSubmit} ]</span>
                    <i className="fa-solid fa-paper-plane"></i>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
